import { useDropzone } from "react-dropzone";
// @mui
import { Box, CircularProgress, Typography } from "@mui/material";
import { alpha, styled, useTheme } from "@mui/material/styles";

import AvatarPreview from "./preview/AvatarPreview";
import { Iconify } from "../iconify";
import { UploadProps } from "./types";

// ----------------------------------------------------------------------

const StyledDropZone = styled(Box)(({ theme }) => ({
  width: 144,
  height: 124,
  margin: "auto",
  display: "flex",
  cursor: "pointer",
  overflow: "hidden",
  alignItems: "center",
  position: "relative",
  justifyContent: "center",
  border: `1px solid ${alpha(theme.palette.grey[500], 0.32)}`,
}));

const StyledPlaceholder = styled(Box)(({ theme }) => ({
  zIndex: 7,
  display: "flex",
  borderRadius: "50%",
  position: "absolute",
  alignItems: "center",
  flexDirection: "column",
  justifyContent: "center",
  width: `calc(100% - 16px)`,
  height: `calc(100% - 16px)`,
  color: theme.palette.text.disabled,
  backgroundColor: theme.palette.background.neutral,
}));

// ----------------------------------------------------------------------

export default function UploadAvatar({
  error,
  file,
  disabled,
  helperText,
  sx,
  loading,
  placeholderSx,
  ...other
}: UploadProps) {
  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      multiple: false,
      disabled,
      accept: { "image/*": ["*"] },
      ...other,
    });

  const theme = useTheme();

  const hasFile = !!file;
  const isError = isDragReject || !!error;

  return (
    <>
      <StyledDropZone
        {...getRootProps()}
        sx={{
          ...(isDragActive && {
            opacity: 0.72,
          }),
          ...(isError && {
            borderColor: theme.palette.error.light,
            ...(hasFile && {
              bgcolor: theme.palette.error.light,
            }),
          }),
          ...(disabled && {
            opacity: 0.48,
            pointerEvents: "none",
          }),
          ...(hasFile && {
            "&:hover": {
              "& .placeholder": {
                opacity: 1,
              },
            },
          }),
          ...sx,
        }}
      >
        <input {...getInputProps()} />

        {hasFile && <AvatarPreview file={file} placeholderSx={placeholderSx} />}

        {loading && <CircularProgress size={25} sx={{ zIndex: 100 }} />}

        <StyledPlaceholder
          className="placeholder"
          sx={{
            "&:hover": {
              opacity: 0.72,
            },
            ...(hasFile && {
              zIndex: 9,
              opacity: 1,
              color: theme.palette.common.white,
              bgcolor: alpha(theme.palette.grey[900], 0.64),
            }),
            ...(isError && {
              color: theme.palette.error.main,
              bgcolor: theme.palette.error.light,
            }),
            ...placeholderSx,
          }}
        >
          <Iconify icon="mingcute:upload-line" />

          <Typography variant="caption" color={theme.palette.info.main}>
            {file ? "Update photo" : "Upload Image"}
          </Typography>
        </StyledPlaceholder>
      </StyledDropZone>

      {helperText && helperText}
    </>
  );
}
